import React from 'react';
import { formatDate } from '../utils';
import type { ChatSessionSummary } from '../../../services/chatApi';
import { useTranslation } from 'react-i18next';

type Props = {
  open: boolean;
  sessions: ChatSessionSummary[];
  activeSessionId?: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

export function Sidebar({ open, sessions, activeSessionId, onSelect, onDelete, onClose }: Props) {
  const { t } = useTranslation();

  const orderedSessions = React.useMemo(
    () =>
      [...sessions].sort((a, b) => {
        const getTime = (session: ChatSessionSummary) => {
          const candidate =
            (session as { updatedAt?: string | null }).updatedAt ??
            (session as { lastActivityTime?: string | null }).lastActivityTime ??
            session.creationTime;
          return candidate ? new Date(candidate).getTime() : 0;
        };

        return getTime(b) - getTime(a);
      }),
    [sessions]
  );

  return (
    <>
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: 260,
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 160ms ease', background: '#f7f7f7', borderRight: '1px solid #eee', zIndex: 10, overflow: 'auto'
      }}>
        <div style={{ padding: 16, borderBottom: '1px solid #eee' }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#222' }}>
            {t('chat.sidebar.title')}
          </h2>
        </div>

        {sessions.length === 0 ? (
          <div style={{ padding: 16, color: '#666', textAlign: 'center' }}>
            {t('chat.sidebar.noSessions')}
          </div>
        ) : (
          orderedSessions.map((s) => (
            <div key={s.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: 12, borderBottom: '1px solid #eee', background: s.id === activeSessionId ? '#e6f0fa' : 'transparent'
            }}>
              <button
                onClick={() => { onSelect(s.id); onClose(); }}
                style={{ textAlign: 'left', flex: 1, background: 'transparent', border: 0, cursor: 'pointer' }}
                title="Open chat"
              >
                <div style={{ fontWeight: 600, color: '#222' }}>{s.name || formatDate(s.creationTime)}</div>
                <div style={{ color: '#666', fontSize: 12 }}>{formatDate(s.creationTime)}</div>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
                style={{ marginLeft: 8, border: 0, background: 'transparent', color: '#d32f2f', cursor: 'pointer' }}
                title={t('chat.sidebar.delete')}
              >
                🗑️
              </button>
            </div>
          ))
        )}
      </div>
      {open && <div onClick={onClose} style={{ position: 'absolute', inset: 0, left: 260, background: 'transparent', zIndex: 9 }} />}
    </>
  );
}